import { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { quizThemePresets, type QuizTheme } from "@/lib/theme-presets";
import { cn } from "@/lib/utils";
import { Heart, Sparkles, Flower } from "lucide-react";

interface ThemeSelectorProps {
  value: string;
  onChange: (theme: QuizTheme) => void;
}

export function ThemeSelector({ value, onChange }: ThemeSelectorProps) {
  // Function to render the appropriate icon based on theme
  const getThemeIcon = (iconType: string) => {
    const iconStyle = { 
      width: "30px", 
      height: "30px", 
      margin: "0 auto" 
    };
    
    switch(iconType) {
      case 'hearts': 
        return <Heart style={iconStyle} />;
      case 'rings': 
        return <Sparkles style={iconStyle} />;
      case 'doves': 
        return <Flower style={iconStyle} />;
      default: 
        return <Heart style={iconStyle} />;
    }
  };
  
  return (
    <RadioGroup
      value={value}
      onValueChange={(themeName) => {
        const theme = quizThemePresets.find((t) => t.name === themeName);
        if (theme) onChange(theme);
      }}
      className="grid grid-cols-1 md:grid-cols-3 gap-6"
    >
      {quizThemePresets.map((theme) => (
        <Label
          key={theme.name}
          className={cn(
            "cursor-pointer transition-all duration-200 hover:scale-[1.02]",
            value === theme.name && "ring-2 ring-offset-2 scale-[1.02]",
            value === theme.name && "ring-primary"
          )}
        >
          <RadioGroupItem value={theme.name} className="sr-only" />
          <Card>
            <CardContent className="p-6 space-y-4">
              {/* Theme colors preview */}
              <div className="space-y-3">
                <div
                  className="w-full h-20 rounded-md relative overflow-hidden flex items-center justify-center"
                  style={{ backgroundColor: theme.primaryColor }}
                >
                  {/* Display the theme icon */}
                  <div className="text-white">
                    {getThemeIcon(theme.welcomeIcon)}
                  </div>
                </div>
                <div
                  className="w-full h-10 rounded-md"
                  style={{ backgroundColor: theme.secondaryColor }}
                />
              </div>
              
              {/* Sample button */}
              <div
                className={cn(
                  "px-4 py-2 text-center text-white font-medium",
                  theme.buttonStyle === "rounded" && "rounded-md",
                  theme.buttonStyle === "square" && "rounded-none",
                  theme.buttonStyle === "pill" && "rounded-full"
                )}
                style={{ backgroundColor: theme.primaryColor }}
              >
                Sample Button
              </div>
              
              {/* Theme information */}
              <div className="space-y-1">
                <div className="text-sm font-medium capitalize">{theme.name}</div>
                <p className="text-xs text-muted-foreground">{theme.description}</p>
              </div>
            </CardContent>
          </Card>
        </Label>
      ))}
    </RadioGroup>
  );
}